// Deleting a retainer — the job delete's dialog one level up.
//
// Same frame (DeleteConfirmModal), same guarantees, wider blast radius. Two things are
// said here that the job version has no need to say:
//
//   1. The JOBS ARE LISTED BY NAME, not just counted. "4 jobs" is a number; the titles
//      are what a partner recognises, and this is the last screen before they are gone.
//   2. A job under a retainer may BILL ITS OWN INCOME (a brokerage margin). Its
//      invoices are not the retainer's monthly billing, so a partner would not expect
//      them to be at stake — which is exactly why they are called out.
//
// It also points at the alternative. Closing a retainer (status = completed) ends the
// engagement and keeps its billing history, and is the right answer for one that simply
// finished; delete is for a retainer that should never have existed. The two acts sit
// one click apart on this screen, so the difference is spelled out rather than assumed.
import useAsync from '../../hooks/useAsync';
import { retainersApi } from '../../api';
import { isProject, engagementStrings } from '../../components/engagement';
import he from '../../i18n/he';
import DeleteConfirmModal from '../../components/DeleteConfirmModal';

const lines = (p, t) => [
  { key: 'jobs', count: p.jobCount, label: t.deleteJobs },
  { key: 'invoices', count: p.invoices, label: he.common.deleteInvoices, amount: p.invoicedTotal },
  { key: 'payments', count: p.payments, label: he.common.deletePayments, amount: p.collectedTotal },
  { key: 'expenses', count: p.expenses, label: he.common.deleteExpenses, amount: p.expenseTotal },
  { key: 'tasks', count: p.tasks, label: he.common.deleteTasks },
  { key: 'publications', count: p.publications, label: he.common.deletePublications },
  { key: 'files', count: p.files, label: he.common.deleteFiles },
  // Its own line rather than folded into the file count above: a signed contract may be
  // the only copy there is, and "12 קבצים" does not warn about it at all.
  { key: 'agreements', count: p.agreements, label: he.common.deleteAgreements },
  // The work content writers sent in (064). Its own line rather than folded into the
  // file count above, for the same reason the contracts have one: nobody here filed it,
  // and a dialog that says '12 files' warns about somebody else's deliverables not at
  // all.
  { key: 'workFiles', count: p.workFiles, label: he.common.deleteWorkFiles },
].filter((line) => line.count > 0);

// Each job by name, badged with whether it merely costs money or also bills for
// itself — the distinction that decides whether its own invoices are going too.
const JobList = ({ jobs }) => (
  <div className="w-list">
    <p>{he.retainers.deleteJobsHeading}</p>
    <ul>
      {jobs.map((job) => (
        <li key={job.id}>
          {job.title}{' '}
          <span className={`badge ${job.income_source === 'own' ? 'blue' : 'gray'}`}>
            {job.income_source === 'own' ? he.retainers.topicBadgeBilling : he.retainers.topicBadgeCost}
          </span>
        </li>
      ))}
    </ul>
  </div>
);

export default function DeleteRetainerModal({ retainer, onClose, onDeleted }) {
  const { data: preview, loading, error } = useAsync(
    () => retainersApi.deletionPreview(retainer.id), [retainer.id]
  );

  return (
    <DeleteConfirmModal
      title={isProject(retainer) ? he.projects.deleteProject : he.retainers.deleteRetainer}
      loading={loading}
      error={error}
      name={preview?.title}
      subtitle={preview?.client_name}
      scopeNote={engagementStrings(retainer).deleteScope}
      items={preview ? lines(preview, engagementStrings(retainer)) : []}
      note={preview && (preview.payments > 0 || preview.expenses > 0)
        ? he.retainers.deleteBalanceNote
        : null}
      onConfirm={() => retainersApi.remove(retainer.id)}
      onClose={onClose}
      onDeleted={onDeleted}
    >
      {preview && preview.jobs.length > 0 && <JobList jobs={preview.jobs} />}
      {preview && preview.billingJobCount > 0 && (
        <p className="error-text">{he.retainers.deleteBillingJobsWarning}</p>
      )}
      {/* Offered before the red button, not after: the reversible option should be
          read while there is still a decision to make. */}
      <p className="muted small-note">{he.retainers.deleteCloseInstead}</p>
    </DeleteConfirmModal>
  );
}
